import {
  Box,
  Flex,
  Heading,
  Modal as ChakraModal,
  ModalContent as ChakraModalContent,
  ModalHeader as ChakraModalHeader,
  ModalOverlay as ChakraModalOverlay,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useKey, useKeyPressEvent } from "react-use";
import { useRouter } from "@tanstack/react-router";
import type {
  ModalProps,
  ModalButtonsProps,
} from "components/modal/modalTypes";
import { Button } from "../button/Button";
import { BOX_SHADOW_VARIANT } from "components/constants/style/boxShadow";
import { IconButton } from "../button/IconButton";

export const ModalButtons = ({
  primaryCTA,
  secondaryCTA,
  isDangerous = false,
  disableEnterKey,
  buttonProps,
  ...rest
}: ModalButtonsProps) => {
  const smallScreen = useBreakpointValue(
    { base: true, lg: false },
    { ssr: false },
  );

  const buttonWidthProps = {
    w: smallScreen ? "100%" : undefined,
    maxW: smallScreen ? "none" : undefined,
  };

  useKeyPressEvent("Enter", () => {
    if (
      !disableEnterKey &&
      primaryCTA &&
      !primaryCTA.isDisabled &&
      primaryCTA.onClick
    ) {
      primaryCTA.onClick("event");
    }
  });

  return (
    <Box
      display="flex"
      flexDirection={smallScreen ? "column-reverse" : "row"}
      justifyContent="center"
      mt="6"
      gap="2"
      {...rest}
    >
      {secondaryCTA && (
        <Button
          variant="secondary"
          sx={isDangerous ? { color: "fg.default" } : {}}
          size="md"
          onClick={secondaryCTA.onClick}
          {...buttonWidthProps}
          flex="1"
          type={secondaryCTA.type}
          isLoading={secondaryCTA.isLoading}
          isDisabled={secondaryCTA.isDisabled}
          data-cy={secondaryCTA["data-cy"] || "modal-button-cancel"}
          icon={secondaryCTA.leftIcon || secondaryCTA.rightIcon}
          isIconOnRight={secondaryCTA.rightIcon ? true : false}
          {...buttonProps}
        >
          {secondaryCTA.text}
        </Button>
      )}
      {primaryCTA && (
        <Button
          variant={isDangerous ? "danger" : "primary"}
          size="md"
          flex="1"
          type={primaryCTA.type}
          onClick={primaryCTA.onClick}
          isLoading={primaryCTA.isLoading}
          isDisabled={primaryCTA.isDisabled}
          data-cy={primaryCTA["data-cy"] || "modal-button-confirm"}
          icon={primaryCTA.leftIcon || primaryCTA.rightIcon}
          isIconOnRight={primaryCTA.rightIcon ? true : false}
          {...buttonProps} // 🤔 can we potentially break something here?
          {...buttonWidthProps}
        >
          {primaryCTA.text}
        </Button>
      )}
    </Box>
  );
};

/** Contains nothing but a H4 header and a close button */
const ModalHeading = ({
  title,
  hasCloseButton = true,
  onClose,
  hasBackButton,
  onBack,
  bannerImage,
}: Pick<
  ModalProps,
  | "title"
  | "onClose"
  | "hasCloseButton"
  | "bannerImage"
  | "size"
  | "onBack"
  | "hasBackButton"
>) => {
  const router = useRouter();

  return (
    <>
      <Box
        w="100%"
        display="flex"
        flexDir="column"
        alignItems="flex-end"
        marginBottom="2"
      >
        <Flex
          justifyContent={onBack || hasBackButton ? "space-between" : "end"}
          w="100%"
        >
          {(onBack || hasBackButton) && (
            <IconButton
              iconName="actions/Arrow/Simple/Left"
              variant="ghost"
              label="Back"
              onClick={onBack || router.history.back}
              color="fg.default"
              size="md"
              left="-2"
            />
          )}
          {hasCloseButton && (
            <IconButton
              iconName="actions/Close/Large"
              variant="ghost"
              label="Close"
              onClick={onClose}
              color="fg.emphasized"
              _hover={{ color: "fg.default" }}
              size="md"
              left="2"
            />
          )}
        </Flex>
      </Box>
      {bannerImage && (
        <Box marginX="auto" mb="3">
          {bannerImage}
        </Box>
      )}
      {title && (
        <ChakraModalHeader p="0" mb="3">
          <Heading as="h3" size="h3">
            {title}
          </Heading>
        </ChakraModalHeader>
      )}
    </>
  );
};

/**
 * Focus the user's attention on a task, decision, or workflow that requires
 * completion before returning to the underlying interface. If users need the
 * underlying page visible, use a Right Panel instead.
 */
export const Modal = (props: ModalProps) => {
  const {
    isOpen = false,
    onClose,
    children,
    size = "sm",
    width,
    "data-cy": dataCy,
    overflow = "hidden",
    contentPadding = "4",
  }: // MFAProtected,
  ModalProps = props;
  const smallScreen = useBreakpointValue(
    { base: true, md: false },
    { ssr: false },
  );
  useKey("Esc", onClose);

  return (
    <ChakraModal
      isOpen={isOpen}
      onClose={onClose}
      isCentered={true}
      {...props.customModalProps}
    >
      <ChakraModalOverlay onClick={onClose} />
      <ChakraModalContent
        display="flex"
        bg="bg.container"
        borderColor="border.container"
        borderRadius="2xl"
        overflow={overflow}
        boxShadow={BOX_SHADOW_VARIANT.lg}
        maxWidth="unset"
        w={
          width
            ? width
            : smallScreen && size === "sm"
              ? "327px"
              : size === "sm"
                ? "400px"
                : size === "md"
                  ? "500px"
                  : size === "xl"
                    ? "640px"
                    : "600px"
        }
        pt={smallScreen ? "6" : "4"}
        px="6"
        pb="8"
        textAlign="center"
        flexDirection="column"
        color="fg.emphasized"
        data-cy={dataCy || "modal"}
      >
        <ModalHeading {...props} />
        <Box px={contentPadding}>{children}</Box>
      </ChakraModalContent>
    </ChakraModal>
  );
};

export type { ModalProps };
